import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import {
  getGrantFullDetails,
  saveManualQA,
  askAIForQA,
  updateQA,
  deleteQA,
  exportFullPdf,
  type FullDetailsResponse,
} from "../../api/grantWriting";
import { postGrantFullDetails } from "../../api/discovery";
import { useAuth } from "../../contexts/AuthContext";
import PrivateFundingWorkspace from "../../features/privateFunding/PrivateFundingWorkspace";
import {
  getActivePrivateFunding,
  setActivePrivateFunding,
} from "../../features/privateFunding/service";
import type { PrivateFundingOpportunity } from "../../features/privateFunding/types";

type QAStatus = "idle" | "saving" | "saved" | "error";

type QAPair = {
  qa_id: number | null;
  question: string;
  answer: string;
  status: QAStatus;
  word_limit: number;
};

function MiniBullet({ n, text }: { n: number; text: string }) {
  return (
    <div className="flex items-start ml-6 mb-6 gap-3 text-md font-medium items-center">
      <div className="h-6 w-6 flex items-center justify-center rounded-full border text-[#202220] border-black bg-black">
        <span className="text-white">{n}</span>
      </div>
      <span className="leading-snug">{text || "—"}</span>
    </div>
  );
}

function StatusPill({ status }: { status: QAStatus }) {
  if (status === "saving") {
    return (
      <div className="flex items-center gap-1 text-[#777B7F]">
        <span className="material-icons text-[16px] animate-spin">hourglass_bottom</span>
        <span>Saving…</span>
      </div>
    );
  }
  if (status === "saved") {
    return (
      <div className="flex items-center gap-1 text-[#4CAF50]">
        <span className="material-icons text-[16px]">check</span>
        <span>Saved</span>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="flex items-center gap-1 text-red-600">
        <span className="material-icons text-[16px]">error_outline</span>
        <span>Error</span>
      </div>
    );
  }
  return null;
}

function ActionPill({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#E5E7EB] bg-white text-[12.5px] hover:bg-black/[0.02] disabled:opacity-50"
    >
      <span className="material-icons text-[18px] text-[#5FA66A]">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function QuestionHeaderRow({
  index,
  questionText,
  onDelete,
}: {
  index: number;
  questionText: string;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4">
      <div className="text-md font-medium pr-4">
        {index + 1}. {questionText || "Untitled question"}
      </div>
      <button
        onClick={onDelete}
        className="text-[#9CA3AF] hover:text-red-600 flex items-center gap-1 text-sm"
        title="Delete question"
      >
        <span className="material-icons text-[18px] leading-none">delete</span>
      </button>
    </div>
  );
}

function QuestionCard({
  idx,
  qa,
  wordCount,
  onAnswerChange,
  onAskAI,
  onCopy,
  onSave,
  onDelete,
  onWordLimitChange,
}: {
  idx: number;
  qa: QAPair;
  wordCount: number;
  onAnswerChange: (val: string) => void;
  onAskAI: (wordLimit: number) => void;
  onCopy: () => void;
  onSave: () => void;
  onDelete: () => void;
  onWordLimitChange: (limit: number) => void;
}) {
  const [wordLimit, setWordLimit] = useState<number>(qa.word_limit);
  const [limitSaved, setLimitSaved] = useState(true);

  const handleSaveLimit = () => {
    const validLimit = wordLimit < 50 ? 50 : wordLimit > 500 ? 500 : wordLimit;
    setWordLimit(validLimit);
    onWordLimitChange(validLimit);
    setLimitSaved(true);
    toast.success(`Word limit set to ${validLimit}${wordLimit > 500 || wordLimit < 50 ? " (range is 50–500)" : ""}`);
  };

  const handleAskAI = () => {
    const currentLimit = wordLimit || 150;
    const validLimit = currentLimit < 50 ? 50 : currentLimit > 500 ? 500 : currentLimit;
    if (!limitSaved || wordLimit !== validLimit) {
      setWordLimit(validLimit);
      onWordLimitChange(validLimit);
      setLimitSaved(true);
    }
    onAskAI(validLimit);
  };

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white shadow-sm">
      <QuestionHeaderRow index={idx} questionText={qa.question} onDelete={onDelete} />
      <div className="px-5 pb-4">
        <textarea
          value={qa.answer}
          onChange={(e) => onAnswerChange(e.target.value)}
          rows={6}
          placeholder="start writing...."
          className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-3 text-[13.5px] outline-none focus:border-[#C7CBCF] resize-y"
        />
        <div className="mt-2 flex items-center justify-between text-sm text-[#777B7F]">
          <div>Word count: {wordCount}</div>
          <StatusPill status={qa.status} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <ActionPill icon="auto_awesome" label="Ask AI" onClick={handleAskAI} />
          <ActionPill icon="content_copy" label="Copy" onClick={onCopy} />
          <ActionPill icon="save" label="Save" onClick={onSave} disabled={qa.status === "saving"} />
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={wordLimit || ""}
              min={0}
              max={500}
              placeholder="Word limit"
              className="h-9 w-[120px] rounded-md border border-gray-200 px-2 text-sm"
              onChange={(e) => {
                const num = Number(e.target.value);
                setWordLimit(num);
                setLimitSaved(false);
              }}
            />
            <button
              onClick={handleSaveLimit}
              className={`h-8 px-3 rounded-md border border-primary text-sm text-white disabled:opacity-60 ${limitSaved ? "bg-primary" : "bg-primary"}`}
            >
              {limitSaved ? "Saved Limit" : "Save limit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GRANT DETAILS MODAL
// ✅ FIX: uses grant_id field (not grant) from saved_grant object
// ─────────────────────────────────────────────────────────────

const GrantDetailsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  discoveryGrantId?: string;
  grantTitle?: string;
}> = ({ open, onClose, discoveryGrantId, grantTitle }) => {
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<any>(null);

  React.useEffect(() => {
    if (!open) return;
    let alive = true;

    // ✅ Guard: if no discoveryGrantId, show "not available" without crashing
    if (!discoveryGrantId) {
      setErr("Grant details are not available for this application.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setErr(null);
        setDetail(null);
        const data = await postGrantFullDetails(discoveryGrantId);
        if (!alive) return;
        setDetail(data);
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to load grant details");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [open, discoveryGrantId]);

  // Reset on close
  React.useEffect(() => {
    if (!open) { setDetail(null); setErr(null); setLoading(false); }
  }, [open]);

  if (!open) return null;

  const prettyDate = (iso?: string | null) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  const title = grantTitle || detail?.title || "Grant Details";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-scroll p-6 pt-0 relative">
        <div className="sticky top-0 bg-white z-10 flex justify-between items-center py-3">
          <h2 className="text-xl font-semibold text-gray-800">Grant Details</h2>
          <button onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        {loading && (
          <div className="space-y-3 py-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 bg-gray-200 animate-pulse rounded w-full" />
            ))}
          </div>
        )}

        {/* ✅ No grant ID — friendly message */}
        {!loading && err && !detail && (
          <div className="py-6 text-center">
            <span className="material-icons text-[40px] text-gray-300 mb-2 block">info</span>
            <p className="text-sm text-gray-500">{err}</p>
            <p className="text-xs text-gray-400 mt-1">
              This grant may not have additional details available yet.
            </p>
          </div>
        )}

        {!loading && !err && detail && (
          <div className="space-y-3 text-sm text-gray-700">
            <p><strong>Name:</strong> {title}</p>
            <p><strong>Application Type:</strong> {detail?.applicant_type ?? "—"}</p>
            <p><strong>Description:</strong> {detail?.description ?? "—"}</p>
            <p><strong>Funder:</strong> {detail?.funder ?? "—"}</p>
            <p><strong>Location:</strong> {detail?.location ?? "—"}</p>
            <p><strong>Source Name:</strong> {detail?.source_name ?? "—"}</p>
            <p><strong>Total Fund Amount:</strong> {detail?.total_fund_amount ?? "—"}</p>
            <p><strong>Status:</strong> {detail?.status ?? "—"}</p>
            <p><strong>Open Date:</strong> {prettyDate(detail?.open_date)}</p>
            <p><strong>Deadline:</strong> {prettyDate(detail?.close_date)}</p>
            {!!detail?.url && (
              <p>
                <strong>URL:</strong>{" "}
                <a className="text-[#0077B6] underline" href={detail.url} target="_blank" rel="noreferrer">
                  {detail.url}
                </a>
              </p>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN WRITE COMPONENT
// ─────────────────────────────────────────────────────────────

export default function Write() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ savedGrantId?: string }>();
  const userKey = user?.uid || user?.email || "anonymous";

  const navState = location.state as
    | {
        saved_grant_id?: number;
        grant?: { id: string | number; title?: string };
        details?: FullDetailsResponse;
        privateFundingId?: string;
      }
    | undefined;

  const computedSavedGrantId =
    params.savedGrantId != null
      ? parseInt(params.savedGrantId, 10)
      : navState?.saved_grant_id;

  const mapDetailsToQAPairs = React.useCallback(
    (data: FullDetailsResponse): QAPair[] => {
      return (data.qa_list || []).map((qa) => ({
        qa_id: qa.id ?? null,
        question: qa.question || "",
        answer: qa.answer || "",
        status: "saved" as QAStatus,
        word_limit: qa.word_limit ?? 150,
      }));
    },
    []
  );

  const initialTitle =
    navState?.grant?.title ??
    navState?.details?.saved_grant?.grant_title ??
    "";

  const initialQaList: QAPair[] = navState?.details
    ? mapDetailsToQAPairs(navState.details)
    : [];

  const [savedGrantId] = React.useState<number | undefined>(computedSavedGrantId);
  const [grantTitle, setGrantTitle] = React.useState<string>(initialTitle);
  const [qaList, setQaList] = React.useState<QAPair[]>(initialQaList);

  const [exporting, setExporting] = React.useState(false);
  const [newQuestionText, setNewQuestionText] = React.useState<string>("");
  const [activePrivateFunding, setActivePrivateFundingState] =
    React.useState<PrivateFundingOpportunity | null>(null);

  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);
  const [grantUrl, setGrantUrl] = React.useState<string | null>(
    navState?.details?.saved_grant?.url ?? null
  );

  
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  // ✅ KEY FIX: API returns `grant_id` (string) not `grant` (number)
  // We check all possible field names in priority order
  const rawDiscoveryGrantId =
    navState?.details?.saved_grant?.grant_id ??   // ← primary: "51jypdnLQjz3P3QQllqTZ8"
    navState?.details?.saved_grant?.grant ??       // ← fallback legacy
    navState?.grant?.id;                           // ← nav state fallback

  const discoveryGrantId: string | undefined =
    rawDiscoveryGrantId != null ? String(rawDiscoveryGrantId) : undefined;
    const [apiDiscoveryGrantId, setApiDiscoveryGrantId] = React.useState<string | undefined>(discoveryGrantId);

  // Fetch discovery details for URL + grant details modal
  React.useEffect(() => {
    if (!discoveryGrantId) return;
    (async () => {
      try {
        const detail = await postGrantFullDetails(discoveryGrantId);
        if (detail?.url) setGrantUrl(detail.url);
      } catch (err) {
        // silently fail — URL is optional
      }
    })();
  }, [discoveryGrantId]);

  React.useEffect(() => {
    const selected = getActivePrivateFunding(userKey);
    setActivePrivateFundingState(selected);
  }, [userKey, navState?.privateFundingId]);

  // Load full grant details if not provided via nav state
  // ── Step 1: Load full grant details (QA list + grant_id) ──
React.useEffect(() => {
  if (!savedGrantId) return;
  if (qaList.length > 0 && grantTitle && apiDiscoveryGrantId) return; // all loaded

  (async () => {
    try {
      setLoadingDetails(true);
      setLoadErr(null);
      const data = await getGrantFullDetails(savedGrantId);

      if (data?.saved_grant?.url) {
        setGrantUrl(data.saved_grant.url);
      }

      if (!grantTitle && data.saved_grant?.grant_title) {
        setGrantTitle(data.saved_grant.grant_title);
      }

      // ✅ Extract grant_id from the API response
      const extractedGrantId =
        String((data?.saved_grant as any)?.grant_id ?? "").trim() ||
        String((data?.saved_grant as any)?.grant ?? "").trim() ||
        undefined;

      if (extractedGrantId) {
        setApiDiscoveryGrantId(extractedGrantId);
      }

      setQaList(mapDetailsToQAPairs(data));
    } catch (err: any) {
      setLoadErr(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message || err?.response?.data?.detail||
          "Failed to load grant details"
      );
    } finally {
      setLoadingDetails(false);
    }
  })();
}, [savedGrantId]); // ✅ only depend on savedGrantId — run once on mount


// ── Step 2: Once we have any discoveryGrantId, fetch the URL ──
const resolvedDiscoveryGrantId = discoveryGrantId || apiDiscoveryGrantId;

React.useEffect(() => {
  if (!resolvedDiscoveryGrantId) return;
  if (grantUrl) return; // already have it

  (async () => {
    try {
      const detail = await postGrantFullDetails(resolvedDiscoveryGrantId);
      if (detail?.url) setGrantUrl(detail.url);
    } catch {
      // URL is optional — fail silently
    }
  })();
}, [resolvedDiscoveryGrantId]); // ✅ re-runs when apiDiscoveryGrantId is set

  // Also fetch grant_id from API response when loading details


  React.useEffect(() => {
    if (!savedGrantId || apiDiscoveryGrantId) return;
    (async () => {
      try {
        const data = await getGrantFullDetails(savedGrantId);
        const grantIdFromApi =
          String((data?.saved_grant as any)?.grant_id ?? (data?.saved_grant as any)?.grant ?? "").trim();
        if (grantIdFromApi) setApiDiscoveryGrantId(grantIdFromApi);
      } catch {}
    })();
  }, [savedGrantId, apiDiscoveryGrantId]);

  // Final resolved discovery grant ID (from nav state OR fetched from API)
  const handleAddQuestion = () => {
    const text = newQuestionText.trim();
    if (!text) return;
    const newPair: QAPair = { qa_id: null, question: text, answer: "", status: "idle", word_limit: 150 };
    setQaList((prev) => [...prev, newPair]);
    setNewQuestionText("");
  };

  const handleAnswerChange = (index: number, value: string) => {
    setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, answer: value } : qa)));
  };

  const handleWordLimitChange = (index: number, limit: number) => {
    setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, word_limit: limit } : qa)));
  };

  const handleCopy = (index: number) => {
    const text = qaList[index]?.answer || "";
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success("Answer copied successfully"))
        .catch(() => toast.error("Failed to copy"));
    }
  };

  const handleAskAI = async (index: number, wordLimit: number) => {
    const item = qaList[index];
    if (!savedGrantId || !item.question.trim()) return;
    setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, status: "saving" as QAStatus } : qa)));
    try {
      const resp = await askAIForQA({ saved_grant_id: savedGrantId, question: item.question, use_ai: true, word_limit_by_ai: wordLimit });
      setQaList((prev) =>
        prev.map((qa, i) =>
          i === index
            ? { ...qa, qa_id: resp.qa_id ?? qa.qa_id ?? null, answer: resp.answer ?? qa.answer, word_limit: wordLimit, status: "saved" as QAStatus }
            : qa
        )
      );
      toast.success("AI response generated!");
    } catch (err: any) {
      setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, status: "error" as QAStatus } : qa)));
      toast.error(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to generate AI response");
    }
  };

  const handleSave = async (index: number) => {
    const item = qaList[index];
    if (!savedGrantId || !item.question.trim()) return;
    setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, status: "saving" as QAStatus } : qa)));
    try {
      if (item.qa_id == null) {
        const resp = await saveManualQA({ saved_grant_id: savedGrantId, question: item.question, answer: item.answer });
        setQaList((prev) =>
          prev.map((qa, i) =>
            i === index
              ? { ...qa, qa_id: resp.qa_id, question: resp.question ?? qa.question, answer: resp.answer ?? qa.answer, status: "saved" as QAStatus }
              : qa
          )
        );
      } else {
        await updateQA(item.qa_id, { question: item.question, answer: item.answer });
        setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, status: "saved" as QAStatus } : qa)));
      }
      toast.success("Saved successfully!");
    } catch {
      setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, status: "error" as QAStatus } : qa)));
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (index: number) => {
    const item = qaList[index];
    if (item.qa_id == null) { setQaList((prev) => prev.filter((_, i) => i !== index)); return; }
    try {
      await deleteQA(item.qa_id);
      setQaList((prev) => prev.filter((_, i) => i !== index));
      toast.success("Question deleted");
    } catch {
      setQaList((prev) => prev.map((qa, i) => (i === index ? { ...qa, status: "error" as QAStatus } : qa)));
      toast.error("Failed to delete");
    }
  };

  const handleExport = async () => {
    if (!savedGrantId) { toast.error("No grant selected to export."); return; }
    try {
      setExporting(true);
      const blob = await exportFullPdf(savedGrantId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `grant-${savedGrantId}-full.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported PDF");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Export failed");
    } finally { setExporting(false); }
  };

  const wc = (txt: string) => {
    const m = txt.trim().match(/\b[\w''-]+\b/g);
    return m ? m.length : 0;
  };

  const bullets = qaList.map((q) => q.question);

  return (
    <div className="mx-auto px-5 xl:px-7 py-6">
      {/* Page heading */}
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-[32px] font-semibold text-[#111]">Grant Writer</h1>
          <p className="text-md text-[#6B7280]">Write the content for the grant questions.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/dashboard/discover")}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-[#0F172A] px-4 py-2 text-sm font-medium text-white"
        >
          <span className="material-icons text-[18px]">add</span>
          <span>Add private opportunity</span>
        </button>
      </div>

      <div className="mb-6">
        <PrivateFundingWorkspace
          userKey={userKey}
          title="Private funding for Grant Writer"
          subtitle="Bring manual or private opportunities into your writing workspace."
          onUseInGrantWriter={(item) => {
            setActivePrivateFunding(userKey, item.id);
            setActivePrivateFundingState(item);
            toast.success("Private opportunity loaded into Grant Writer.");
          }}
        />
      </div>

      {activePrivateFunding && (
        <div className="mb-6 rounded-3xl border border-[#D6F5DD] bg-[#F3FBF4] p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.08em] text-[#166534]">
                Active private opportunity
              </div>
              <div className="mt-1 text-xl font-semibold text-[#0F172A]">
                {activePrivateFunding.funderName}
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-sm text-[#475569]">
                {activePrivateFunding.amount ? <span>Amount: {activePrivateFunding.amount}</span> : null}
                {activePrivateFunding.deadline ? <span>Deadline: {activePrivateFunding.deadline}</span> : null}
                <span className="rounded-full bg-white px-3 py-1 text-[#166534] shadow-sm">
                  Private
                </span>
              </div>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-[#475569]">
                {activePrivateFunding.whatTheyAreLookingFor || activePrivateFunding.summary}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setActivePrivateFunding(userKey, null);
                setActivePrivateFundingState(null);
              }}
              className="inline-flex min-h-[40px] items-center rounded-full border border-[#BFE4C5] bg-white px-4 py-2 text-sm font-medium text-[#166534]"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Top "brief" card */}
      <div className="rounded-xl border border-[#E5E7EB] bg-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between px-5 pt-4 gap-4">
          {/* ✅ Grant title — link only if URL exists */}
          {grantUrl ? (
            <a
              className="text-[20px] font-bold flex-1 text-[#0077B6] hover:underline"
              href={grantUrl}
              target="_blank"
              rel="noreferrer"
            >
              {grantTitle || "Loading..."}
            </a>
          ) : (
            <div className="text-[20px] font-bold flex-1">{grantTitle || "Loading..."}</div>
          )}

          {/* ✅ View Details button — always shows, handles missing ID gracefully */}
          <button
            className="text-md font-medium hover:underline self-start text-[#0077B6]"
            onClick={() => setDetailsOpen(true)}
          >
            View Details
          </button>
        </div>

        {/* Add question row */}
        <div className="px-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4 mt-5">
          <input
            className="w-full h-[60px] rounded-lg border border-[#E5E7EB] bg-white px-3 text-[13.5px] outline-none focus:border-[#C7CBCF]"
            placeholder="write here"
            value={newQuestionText}
            onChange={(e) => setNewQuestionText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddQuestion(); }}
          />
          <div className="flex-shrink-0">
            <button onClick={handleAddQuestion}
              className="py-2 w-[150px] px-3 rounded-md bg-primary text-sm font-medium shadow-sm hover:brightness-95">
              Add question
            </button>
          </div>
        </div>

        {/* Bullet list */}
        <div className="mt-5 pb-4">
          {bullets.map((text, idx) => (
            <MiniBullet key={idx} n={idx + 1} text={text || ""} />
          ))}
        </div>
      </div>

      {/* Grant Response header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 mt-4 gap-3">
        <h2 className="text-[32px] font-semibold text-[#111]">Grant Response</h2>
        <button
          className="h-8 px-3 rounded-md border border-primary bg-primary text-sm hover:bg-primary/50 text-white disabled:opacity-60"
          onClick={handleExport}
          disabled={!savedGrantId || exporting}
        >
          {exporting ? "Exporting…" : "Export as PDF"}
        </button>
      </div>

      {loadingDetails && (
        <div className="space-y-3 mb-4">
          {[1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="h-5 bg-gray-200 animate-pulse rounded w-1/3" />x
              <div className="h-24 bg-gray-100 animate-pulse rounded" /> 
            </div>
          ))}
        </div>
      )}
      {loadErr && <div className="text-sm text-red-600 mb-2">{loadErr}</div>} 

      {/* Q&A cards */}
      <div className="mt-3 flex flex-col gap-5">
        {qaList.map((qa, i) => (
          <QuestionCard
            key={qa.qa_id ?? `local-${i}`}
            idx={i}
            qa={qa}
            wordCount={wc(qa.answer)}
            onAnswerChange={(val) => handleAnswerChange(i, val)}
            onAskAI={(wordLimit) => handleAskAI(i, wordLimit)}
            onCopy={() => handleCopy(i)}
            onSave={() => handleSave(i)}
            onDelete={() => handleDelete(i)}
            onWordLimitChange={(limit) => handleWordLimitChange(i, limit)}
          />
        ))}
      </div>
      {/* Next Button */}
<div className="mt-10 flex justify-end">
  <button
    onClick={() => navigate("/dashboard/organize")}
    className="h-10 px-6 rounded-md bg-primary text-white font-medium hover:brightness-95"
  >
    Next →
  </button>
</div>

      {/* ✅ Grant details modal — uses resolvedDiscoveryGrantId */}
      <GrantDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        discoveryGrantId={resolvedDiscoveryGrantId}
        grantTitle={grantTitle}
      />
    </div>
  );
}
